/**
 * V70G8J-2 — 팀전 대진표 팀원 목록 팝오버 (참가자용 대진표 전용)
 *
 * 정책: 게임대회 접근 권한자에게 팀원 "이름"만 공개. 데이터는 서버 getBracketTeamMembers 응답을 그대로 표시.
 * 동작
 *   - PC: 팀명에 마우스를 올리면 열림 / 벗어나면 닫힘. 클릭하면 고정(열림 유지), 고정 상태에서 다시 클릭하면 닫힘
 *   - 모바일(터치): 탭으로 열기/닫기 (hover 미사용)
 *   - 키보드: 팀명 버튼에서 Enter/Space 로 열기/닫기, Esc 로 닫고 팀명으로 포커스 복귀
 *   - 다른 팀 선택 시 그 팀 목록으로 교체, 바깥 클릭 시 닫힘
 *   - 화면 기준(fixed) 위치 + body portal → 대진표 가로 스크롤 영역·카드에 잘리지 않음, 화면 가장자리에서 안쪽으로 보정
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../../firebase/utils';
import { DARK } from './tournamentDarkTheme';

export const TEAM_MEMBERS_EMPTY_TEXT = '팀원 정보가 없습니다';
export const TEAM_MEMBERS_ERROR_TEXT = '팀원 정보를 불러오지 못했습니다.';

type LoadState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; byPublicId: Record<string, string[]> }
  | { status: 'error' };

/** 대진표 단위 1회 조회 (처음 열 때). 대진표가 바뀌면 초기화 */
export function useBracketTeamMembers(bracketId: string) {
  const [state, setState] = useState<LoadState>({ status: 'idle' });
  const reqRef = useRef<string | null>(null);
  useEffect(() => { setState({ status: 'idle' }); reqRef.current = null; }, [bracketId]);
  const ensureLoaded = useCallback(() => {
    if (reqRef.current === bracketId) return;
    reqRef.current = bracketId;
    setState({ status: 'loading' });
    httpsCallable<{ bracketId: string }, { byPublicId: Record<string, string[]> }>(getFunctions(), 'getBracketTeamMembers')({ bracketId })
      .then((r) => {
        if (reqRef.current !== bracketId) return;
        setState({ status: 'ready', byPublicId: r.data?.byPublicId || {} });
      })
      .catch((e) => {
        if (reqRef.current !== bracketId) return;
        console.warn('[bracket] team members load failed:', e);
        reqRef.current = null; // 다음 열기 때 재시도
        setState({ status: 'error' });
      });
  }, [bracketId]);
  return { state, ensureLoaded };
}

export interface OpenTeam {
  publicParticipantId: string;
  teamName: string;
  anchor: HTMLElement;
  pinned: boolean;
}

/** 팀명 버튼 (SideRow 안 팀명 자리) */
export function TeamNameTrigger({
  publicParticipantId, teamName, open, onHoverOpen, onHoverLeave, onToggle, children,
}: {
  publicParticipantId: string;
  teamName: string;
  open: boolean;
  onHoverOpen: (anchor: HTMLElement) => void;
  onHoverLeave: () => void;
  onToggle: (anchor: HTMLElement) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-team-trigger={publicParticipantId}
      data-testid={`team-trigger-${publicParticipantId}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? `team-members-${publicParticipantId}` : undefined}
      aria-label={`${teamName} 팀원 보기`}
      title={teamName}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') onHoverOpen(e.currentTarget); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') onHoverLeave(); }}
      onClick={(e) => { e.stopPropagation(); onToggle(e.currentTarget); }}
      style={triggerStyle}
    >
      {children}
    </button>
  );
}

const MARGIN = 8;
const MAX_WIDTH = 280;

/** 앵커 기준 화면 안 위치 계산 (아래 우선, 공간 없으면 위) */
export function placePopover(
  anchor: { left: number; top: number; bottom: number; width: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
): { left: number; top: number } {
  const w = Math.min(size.width, viewport.width - MARGIN * 2);
  let left = anchor.left;
  if (left + w > viewport.width - MARGIN) left = viewport.width - MARGIN - w;
  if (left < MARGIN) left = MARGIN;
  const below = anchor.bottom + 4;
  const above = anchor.top - 4 - size.height;
  let top = below;
  if (below + size.height > viewport.height - MARGIN && above >= MARGIN) top = above;
  if (top + size.height > viewport.height - MARGIN) top = Math.max(MARGIN, viewport.height - MARGIN - size.height);
  return { left: Math.round(left), top: Math.round(top) };
}

export function TeamMembersPopover({
  open, state, onClose, onPopoverEnter, onPopoverLeave, theme = 'light',
}: {
  /** V70-M — 'dark': 게임대회 다크 테마 대진표 상세. 기본 'light' (기존 표시) */
  theme?: 'light' | 'dark';
  open: OpenTeam;
  state: LoadState;
  onClose: (restoreFocus: boolean) => void;
  onPopoverEnter: () => void;
  onPopoverLeave: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const dark = theme === 'dark';
  const c = dark
    ? { title: DARK.text, sub: DARK.textMuted, list: DARK.text, error: '#FCA5A5' }
    : { title: '#0f172a', sub: '#64748b', list: '#1e293b', error: '#b91c1c' };

  const reposition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = open.anchor.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    // 앵커가 화면 밖으로 완전히 나가면 닫기 (스크롤)
    if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) { onClose(false); return; }
    setPos(placePopover(r, { width: el.offsetWidth || MAX_WIDTH, height: el.offsetHeight || 0 }, { width: vw, height: vh }));
  }, [open.anchor, onClose]);

  useLayoutEffect(() => { reposition(); }, [reposition, state, open.publicParticipantId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(true); } };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (ref.current?.contains(t)) return;
      if (t instanceof Element && t.closest('[data-team-trigger]')) return; // 트리거 클릭은 토글이 처리
      onClose(false);
    };
    const onMove = () => reposition();
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [onClose, reposition]);

  const names = state.status === 'ready' ? (state.byPublicId[open.publicParticipantId] || []) : [];
  return createPortal(
    <div
      ref={ref}
      id={`team-members-${open.publicParticipantId}`}
      role="dialog"
      aria-label={`${open.teamName} 팀원`}
      data-testid="team-members-popover"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') onPopoverEnter(); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') onPopoverLeave(); }}
      style={{
        ...popoverStyle,
        ...(dark ? { background: DARK.surface, border: `1px solid ${DARK.border}`, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)' } : null),
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 13, color: c.title, marginBottom: 6, overflowWrap: 'anywhere' }}>
        {open.teamName}
      </div>
      <div style={{ fontSize: 11, color: c.sub, marginBottom: 4 }}>팀원</div>
      {state.status === 'ready' ? (
        names.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: c.list, lineHeight: 1.6 }} data-testid="team-members-list">
            {names.map((n, i) => (
              // 동명이인이 있을 수 있어 순번 포함 key
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${i}-${n}`} style={{ overflowWrap: 'anywhere' }}>{n}</li>
            ))}
          </ul>
        ) : (
          <div style={{ fontSize: 12, color: c.sub }}>{TEAM_MEMBERS_EMPTY_TEXT}</div>
        )
      ) : state.status === 'error' ? (
        <div style={{ fontSize: 12, color: c.error }}>{TEAM_MEMBERS_ERROR_TEXT}</div>
      ) : (
        <div style={{ fontSize: 12, color: c.sub }}>불러오는 중…</div>
      )}
    </div>,
    document.body,
  );
}

const triggerStyle: React.CSSProperties = {
  // 기존 이름 span 과 같은 모양 유지 (글자색·굵기는 부모 span 상속)
  font: 'inherit', color: 'inherit', background: 'transparent', border: 0, padding: 0, margin: 0,
  cursor: 'pointer', textAlign: 'left', maxWidth: '100%',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  textDecoration: 'underline dotted', textUnderlineOffset: 3,
};
const popoverStyle: React.CSSProperties = {
  position: 'fixed', zIndex: 1000, boxSizing: 'border-box',
  minWidth: 160, maxWidth: `min(${MAX_WIDTH}px, calc(100vw - ${MARGIN * 2}px))`,
  maxHeight: '60vh', overflowY: 'auto',
  background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)', padding: '10px 12px',
};
